import React from 'react';
import {object} from 'prop-types';
import {
  COLORS,
  SIZES,
  LoadingIndicator,
  List,
  Placeholder,
  ExpansionHeader,
  ToggleIcon,
  ExpansionBody,
} from '@fupa/fupa-uikit';
import {LinkOnComponent} from 'app/components/links/link';
import styled from 'styled-components';
import {LinkToCompetition} from 'app/components/links/LinkToCompetition';
import {Helmet} from 'react-helmet-async';
import {TITLE_POSTFIX} from 'app/seo/seo.constants';
import useDistrictUrls from 'app/hooks/useDistrictUrls';

const Competition = styled(List)`
  padding-left: ${SIZES['52']};
`;

const StyledPlaceholder = styled(Placeholder)`
  &&& {
    background-color: transparent;
  }
`;

const competitionRowHeight = 42;
const loadingIndicatorHeight = 90;

const metaCompetitionTitle = {
  1: 'Ligen',
  2: 'Hallenturniere',
  3: 'Pokale',
  4: 'Turniere',
  5: 'Relegation',
};

const metaCompetitionDesc = {
  1: 'Ligen',
  2: 'Hallenturniere',
  3: 'Pokalwettbewerbe',
  4: 'Sommerturniere',
  5: 'Relegationsturniere',
};

const AgeGroupRow = ({
  urlWithoutSubPath,
  competitionsState,
  ageGroup,
  districtSlug,
  competitionCategoryAgeGroup,
  districtName,
  openAgeGroup,
  setOpenAgeGroup,
  initRender,
}) => {
  const rowAgeGroupSlug = ageGroup.slug;
  const category = competitionCategoryAgeGroup.id;
  const destination = category === 1 ? 'league' : 'cup';
  const key = `${category}_${rowAgeGroupSlug}`;
  const {isFetching, error} = competitionsState;
  const competitions = key in competitionsState ? competitionsState[key] : [];
  const isActiveAgeGroup = openAgeGroup === key;
  const {districtCompetitionsUrl} = useDistrictUrls(districtSlug);

  const toggleAgeGroupRow = () => {
    if (isActiveAgeGroup) {
      setOpenAgeGroup('');
    } else {
      setOpenAgeGroup(key);
    }
  };

  let helmet = null;
  if (isActiveAgeGroup && !isFetching && !error && !urlWithoutSubPath) {
    const title = `${ageGroup.name}-${metaCompetitionTitle[category]}${TITLE_POSTFIX} ${districtName}${TITLE_POSTFIX}`;
    const meta = [
      {
        name: 'description',
        content: `Alle aktuellen ${metaCompetitionDesc[category]} aus der Region ${districtName}.`,
      },
    ];

    helmet = <Helmet title={title} meta={meta} />;
  }

  const showCompetitions = isActiveAgeGroup ? (
    isFetching ? (
      <LoadingIndicator message='Wettbewerbe werden geladen...' bgColor={false} />
    ) : error ? (
      <StyledPlaceholder>Beim Laden ist ein Fehler aufgetreten.</StyledPlaceholder>
    ) : (
      competitions.map(competition => (
        <LinkToCompetition
          competitionType={destination}
          competition={competition}
          enablehover={true.toString()}
          hovercolor={COLORS.lightGrey}
          key={competition.competitionSeasonId}>
          <Competition>{competition.name}</Competition>
        </LinkToCompetition>
      ))
    )
  ) : null;

  const bodyHeight = isActiveAgeGroup
    ? competitions.length
      ? competitions.length * competitionRowHeight
      : isFetching
      ? loadingIndicatorHeight
      : competitionRowHeight
    : 0;

  return (
    <React.Fragment>
      {helmet}
      <LinkOnComponent
        replace
        enablehover={true.toString()}
        onClick={toggleAgeGroupRow}
        to={
          isActiveAgeGroup || urlWithoutSubPath
            ? districtCompetitionsUrl
            : `${districtCompetitionsUrl}/${category}/ageGroup/${rowAgeGroupSlug}`
        }>
        <ExpansionHeader>
          <ToggleIcon init={initRender} addmargin='right' open={isActiveAgeGroup} style={{color: COLORS.darkGrey}} />
          {ageGroup.name}
        </ExpansionHeader>
      </LinkOnComponent>
      <ExpansionBody init={initRender} height={bodyHeight} open={true}>
        {showCompetitions}
      </ExpansionBody>
    </React.Fragment>
  );
};

AgeGroupRow.propTypes = {
  ageGroup: object.isRequired,
};

export default AgeGroupRow;
